/**
 * Custom hook for managing DrivePanel context synchronization
 *
 * Extracted from DrivePanel.tsx
 */

import { useCallback, useRef, useEffect } from 'react';
import type { Document as ApiDocument, PDFSnippet, SavedMessage } from '../../../services/api';
import type { SavedContent } from '../../../services/savedContentService';
import type { MinimalDocument } from '../../../contexts/DocumentSelectionContext';
import type { TableItem } from '../../../services/driveService';
import type {
  UseDriveContextSyncProps,
  UseDriveContextSyncReturn,
} from '../types/drive-panel.types';

/**
 * Manages synchronization between DrivePanel and DocumentSelectionContext
 */
export function useDriveContextSync({
  privateDocuments,
  sharedDocuments,
  selectedDocumentsFromContext,
  selectContextDocuments,
  deselectContextDocuments,
  setContextDocument,
  setAvailableDocumentIds,
}: UseDriveContextSyncProps): UseDriveContextSyncReturn {
  // Use refs to avoid recreating the callback when selectedDocumentsFromContext changes
  // This prevents infinite loops when the callback is used in useEffect dependencies
  const selectedDocumentsFromContextRef = useRef(selectedDocumentsFromContext);
  useEffect(() => {
    selectedDocumentsFromContextRef.current = selectedDocumentsFromContext;
  }, [selectedDocumentsFromContext]);

  // Wrapper for document selection that uses context
  const handleDocumentSelect = useCallback(
    (documentIds: string[]) => {
      // Update context with new selection - use ref to get current value without dependency
      const currentIds = selectedDocumentsFromContextRef.current;
      const toAdd = documentIds.filter(id => !currentIds.includes(id));
      const toRemove = currentIds.filter(id => !documentIds.includes(id));

      if (toAdd.length > 0) {
        selectContextDocuments(toAdd, 'manual');
      }
      if (toRemove.length > 0) {
        deselectContextDocuments(toRemove);
      }
    },
    [selectContextDocuments, deselectContextDocuments]
  );

  // Helper para crear MinimalDocument desde TableItem
  const createMinimalDocumentFromTableItem = useCallback(
    (item: TableItem): MinimalDocument | null => {
      if (item.type === 'document') {
        const doc = item.data as ApiDocument;
        return {
          id: doc.id,
          name: doc.name || doc.original_filename,
          documentType: doc.file_type || 'other',
          scope: (doc.scope as 'private' | 'shared') || 'private',
          original_filename: doc.original_filename,
        };
      } else if (item.type === 'snippet') {
        const snippet = item.data as PDFSnippet;
        return {
          id: snippet.id,
          name: snippet.title || `Snippet - ${snippet.document_name || 'Documento'}`,
          documentType: 'snippet',
          scope: 'private',
          original_filename: snippet.document_filename || snippet.document_name || 'snippet',
        };
      } else if (item.type === 'studio-result') {
        const studio = item.data as SavedContent;
        return {
          id: studio.id,
          name: studio.title || studio.studio_name || 'Resultado Studio',
          documentType: 'studio',
          scope: 'private',
          original_filename: studio.studio_name || 'studio-result',
        };
      } else if (item.type === 'saved-message') {
        const message = item.data as SavedMessage;
        return {
          id: message.id,
          name: message.title || 'Mensaje guardado',
          documentType: 'message',
          scope: 'private',
          original_filename: 'saved-message',
        };
      }
      return null;
    },
    []
  );

  // Sync loaded documents with context for display
  useEffect(() => {
    const allDocs = [...privateDocuments, ...sharedDocuments];
    // Set available document IDs to prevent loading non-existent documents
    setAvailableDocumentIds(allDocs.map(doc => doc.id));
    // Sync document details to context
    allDocs.forEach(doc => {
      setContextDocument({
        id: doc.id,
        name: doc.name,
        documentType: doc.file_type || 'other',
        scope: (doc.scope as 'private' | 'shared') || 'private',
        original_filename: doc.original_filename,
      });
    });
  }, [
    privateDocuments,
    sharedDocuments,
    setContextDocument,
    setAvailableDocumentIds,
  ]);

  return {
    handleDocumentSelect,
    createMinimalDocumentFromTableItem,
  };
}
